/**
 * Precise relative timestamps with two-unit precision.
 *
 * The owner wants "1 hr 15 min ago", not date-fns' coarse "about 1 hour
 * ago". Shared by SolArkSyncPanel and SolArkSyncHistory (and any future
 * last-sync display) so both render identically.
 */

const SEC_MS = 1000;
const MIN_MS = 60 * SEC_MS;
const HR_MS = 60 * MIN_MS;
const DAY_MS = 24 * HR_MS;

/**
 * Format a timestamp as a precise relative phrase:
 * - < 60 s        → "just now"
 * - < 60 min      → "N min ago"
 * - < 24 hr       → "H hr M min ago" ("2 hr ago" when minutes are 0)
 * - < 7 days      → "D day[s] H hr ago" ("3 days ago" when hours are 0)
 * - >= 7 days     → absolute locale date (what the history panel shows)
 *
 * Future timestamps clamp to "just now".
 *
 * @param iso ISO timestamp string or Date
 * @param now Injectable "current time" for deterministic tests
 */
export function formatPreciseAgo(iso: string | Date, now: Date = new Date()): string {
  const date = iso instanceof Date ? iso : new Date(iso);
  const diff = now.getTime() - date.getTime();

  if (diff < 60 * SEC_MS) return 'just now';

  if (diff < 60 * MIN_MS) {
    const minutes = Math.floor(diff / MIN_MS);
    return `${minutes} min ago`;
  }

  if (diff < DAY_MS) {
    const hours = Math.floor(diff / HR_MS);
    const minutes = Math.floor((diff % HR_MS) / MIN_MS);
    return minutes > 0 ? `${hours} hr ${minutes} min ago` : `${hours} hr ago`;
  }

  if (diff < 7 * DAY_MS) {
    const days = Math.floor(diff / DAY_MS);
    const hours = Math.floor((diff % DAY_MS) / HR_MS);
    const dayLabel = days === 1 ? 'day' : 'days';
    return hours > 0 ? `${days} ${dayLabel} ${hours} hr ago` : `${days} ${dayLabel} ago`;
  }

  return date.toLocaleDateString();
}
